<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add new logout view that clears user state and CSRF token on mount.
 *   - Display logout confirmation message.
 *   - No breaking changes; new feature.
 *
 * 2026-03-17 (0.1.1) (jhoward)
 *   - Clear winLossDate, winLoss, info, and offers from appStore.account on logout.
 *
 * 2026-10-10 (0.1.2) (jhoward)
 *   - Also clear winLossStatement and tournamentResults. The account pages are now kept alive and live for longer, so a
 *     copy left in the store could be shown to the next patron to sign in.
 *
 * 2026-10-15 (0.2.0) (jhoward)
 *   - Clear the user and the account data whatever the answer, through appStore.resetAccountState(). Only a 200
 *     cleared them, so a logout that failed (a 419 after a long idle) left the patron's account for the next guest.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import MaterialButton from '@framework/components/material/MaterialButton.vue';
import MaterialFormContainer from '@framework/components/material/MaterialFormContainer.vue';
import type { FetchResponse } from '@framework/defs/framework';
import { onMounted, ref } from 'vue';
import PeppermillViewport from '@/components/framework-proxy/PeppermillViewport.vue';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import useAppStore from '@/store';
import { forgetDevice } from '@rss/actions/App/Http/Controllers/AuthController';
import { logout as logoutRoute } from '@rss/routes/app';

interface LogoutResponse extends FetchResponse {
  data: {
    csrfToken: string;
    honeypot: string;
    canForgetDevice: boolean;
  };
}

const appStore = useAppStore();
const canForgetDevice = ref<boolean>(false);

async function handleForgetDevice() {
  const response = await peppermillCustomFetchProxy(
    {},
    {
      route: forgetDevice(),
    },
  );

  if (response.message) {
    appStore.addSnack({
      message: response.message,
    });
  }

  if (200 === response.status) {
    canForgetDevice.value = false;
  }
}

onMounted(async () => {
  try {
    const response = await peppermillCustomFetchProxy<LogoutResponse>(
      {},
      {
        route: logoutRoute(),
      },
    );

    if (200 === response.status) {
      appStore.state.user = null;
      appStore.csrfToken = response.data.csrfToken;
      appStore.honeypot = response.data.honeypot;
      appStore.state.twoFactorAuthRequired = true;

      canForgetDevice.value = response.data.canForgetDevice;
    }

    if (response.message) {
      appStore.setNewSnack(15000, response.message);
    }
  } catch (error) {
    console.error(error);
    appStore.setNewSnack(15000, 'An error occurred during logout.', 'error');
  } finally {
    //Whatever the server said, this tab is done with this patron.
    appStore.state.user = null;
    appStore.resetAccountState();
  }
});
</script>

<template>
  <peppermill-viewport>
    <material-form-container hide-steps ignore-ajax>
      <peppermill-content-block>
        <p>You have been logged out. <router-link to="login">Login again.</router-link></p>
        <material-button
          v-if="canForgetDevice"
          label="Forget this Device"
          @click.prevent="handleForgetDevice"
        ></material-button>
      </peppermill-content-block>
    </material-form-container>
  </peppermill-viewport>
</template>

<style scoped></style>
